// frontend/src/components/charts/DeviceBreakdown.jsx
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { getCodeDeviceBreakdown } from "../../api/analytics";

// Drill order: each click narrows by the current dimension and advances to the
// next. Browser is the leaf (no further drill-down).
const ORDER = ["device_type", "brand", "os", "browser"];
const LABELS = {
  device_type: "Device type",
  brand: "Brand",
  os: "Operating system",
  browser: "Browser",
};

/** Pretty-print a dimension value (device_type comes back lowercase). */
function fmtValue(dimension, value) {
  if (dimension === "device_type" && value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  return value;
}

/**
 * DeviceBreakdown — interactive device drill-down for one code.
 *
 * Starts at "Device type" and drills Device type → Brand → OS → Browser, each
 * click adding a filter. Breadcrumbs walk back up. Brand/OS/Browser are parsed
 * from the User-Agent; rows that couldn't be attributed show as "Unknown".
 */
function DeviceBreakdown({ code }) {
  const [path, setPath] = useState([]); // [{ dimension, value }]
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const dimension = ORDER[Math.min(path.length, ORDER.length - 1)];
  const atLeaf = path.length >= ORDER.length - 1;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    const filters = Object.fromEntries(path.map((p) => [p.dimension, p.value]));
    getCodeDeviceBreakdown(code, { dimension, ...filters })
      .then((d) => {
        if (active) setRows(d.rows || []);
      })
      .catch(() => {
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // dimension is derived from path, so path is the only real dependency.
  }, [code, path]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const drillInto = (value) => {
    if (!atLeaf) setPath((p) => [...p, { dimension, value }]);
  };
  const crumbTo = (idx) => setPath((p) => p.slice(0, idx));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700">Devices</h2>
        <span className="text-xs font-medium text-gray-400">
          by {LABELS[dimension].toLowerCase()}
        </span>
      </div>

      {/* Breadcrumb */}
      <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-500">
        <button
          type="button"
          onClick={() => crumbTo(0)}
          className={path.length === 0 ? "text-gray-700 font-medium" : "text-blue-600 hover:underline"}
        >
          All
        </button>
        {path.map((p, i) => (
          <span key={`${p.dimension}-${p.value}`} className="flex items-center gap-1">
            <span className="text-gray-300">›</span>
            <button
              type="button"
              onClick={() => crumbTo(i + 1)}
              className={
                i === path.length - 1
                  ? "text-gray-700 font-medium"
                  : "text-blue-600 hover:underline"
              }
            >
              {fmtValue(p.dimension, p.value)}
            </button>
          </span>
        ))}
      </div>

      {/* Rows */}
      <div className="mt-3 space-y-1.5">
        {loading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : error ? (
          <p className="text-sm text-gray-400 py-4 text-center">
            Couldn’t load device data.
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center">
            No device data yet.
          </p>
        ) : (
          rows.map((r) => {
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            const label = fmtValue(dimension, r.value);
            const RowTag = atLeaf ? "div" : "button";
            return (
              <RowTag
                key={r.value}
                type={atLeaf ? undefined : "button"}
                onClick={atLeaf ? undefined : () => drillInto(r.value)}
                className={[
                  "relative block w-full overflow-hidden rounded-md px-3 py-2 text-left",
                  atLeaf ? "cursor-default" : "hover:ring-1 hover:ring-blue-200",
                ].join(" ")}
                title={atLeaf ? undefined : `Drill into ${label}`}
              >
                {/* Proportional bar */}
                <span
                  className="absolute inset-y-0 left-0 bg-blue-50"
                  style={{ width: `${pct}%` }}
                  aria-hidden="true"
                />
                <span className="relative flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2 text-gray-700">
                    {label}
                    {!atLeaf && <span className="text-gray-300">›</span>}
                  </span>
                  <span className="tabular-nums text-gray-500">
                    {r.count.toLocaleString()}
                    <span className="ml-1 text-xs text-gray-400">{pct}%</span>
                  </span>
                </span>
              </RowTag>
            );
          })
        )}
      </div>

      {!atLeaf && rows.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-400">
          Click a row to drill down.
        </p>
      )}
    </div>
  );
}

DeviceBreakdown.propTypes = {
  code: PropTypes.string.isRequired,
};

export default DeviceBreakdown;
